import { PlusIcon } from '@shopify/polaris-icons';
import { Page, TextField, Modal } from '../components/forms';
import { useEffect, useState } from 'react';
import {
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Badge,
  Button,
  DataTable,
  Spinner,
  Banner,
  Checkbox,
  Divider,
} from '@shopify/polaris';
import { useApi } from '../hooks/useApi';

interface AdminPermissions {
  canViewDashboard: boolean;
  canViewShops: boolean;
  canManageShops: boolean;
  canViewUsers: boolean;
  canViewRevenue: boolean;
  canManagePricing: boolean;
  canManageAdmins: boolean;
}

interface SuperAdmin {
  id: number;
  email: string;
  name: string | null;
  added_by: string | null;
  added_at: string | null;
  is_active: number;
  is_owner: boolean;
  permissions: AdminPermissions;
}

const DEFAULT_PERMISSIONS: AdminPermissions = {
  canViewDashboard: true,
  canViewShops: true,
  canManageShops: false,
  canViewUsers: true,
  canViewRevenue: false,
  canManagePricing: false,
  canManageAdmins: false,
};

const PERMISSION_LABELS: Record<keyof AdminPermissions, { label: string; description: string }> = {
  canViewDashboard: { label: 'View Dashboard', description: 'Access the admin dashboard overview' },
  canViewShops: { label: 'View Shops', description: 'See all shops and their details' },
  canManageShops: { label: 'Manage Shops', description: 'Change shop plans and settings' },
  canViewUsers: { label: 'View Users', description: 'See all users across all shops' },
  canViewRevenue: { label: 'View Revenue', description: 'Access revenue analytics and MRR data' },
  canManagePricing: { label: 'Manage Pricing', description: 'Edit subscription plan prices' },
  canManageAdmins: { label: 'Manage Admins', description: 'Add/remove super admins (owner only)' },
};

export default function SuperAdmins() {
  const { fetchApi, error } = useApi();
  const [admins, setAdmins] = useState<SuperAdmin[]>([]);
  const [loading, setLoading] = useState(true);

  // Add admin modal
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [newPermissions, setNewPermissions] = useState<AdminPermissions>(DEFAULT_PERMISSIONS);

  // Edit permissions modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState<SuperAdmin | null>(null);
  const [editPermissions, setEditPermissions] = useState<AdminPermissions>(DEFAULT_PERMISSIONS);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    loadAdmins();
  }, []);

  async function loadAdmins() {
    const response = await fetchApi<{ success: boolean; admins: SuperAdmin[] }>('/super-admin/admins');
    if (response?.success) {
      setAdmins(response.admins);
    }
    setLoading(false);
  }

  async function handleAddAdmin() {
    if (!newEmail.trim()) {
      setSaveError('Email is required');
      return;
    }

    setSaving(true);
    setSaveError(null);

    // First add the admin
    const addResponse = await fetchApi<{ success: boolean; error?: string }>('/super-admin/admins', {
      method: 'POST',
      body: JSON.stringify({
        email: newEmail.trim().toLowerCase(),
        name: newName.trim() || undefined,
      }),
    });

    if (!addResponse?.success) {
      setSaving(false);
      setSaveError(addResponse?.error || 'Failed to add admin');
      return;
    }

    // Reload to get the new admin's ID, then update permissions
    const reloadResponse = await fetchApi<{ success: boolean; admins: SuperAdmin[] }>('/super-admin/admins');
    if (reloadResponse?.success) {
      const newAdmin = reloadResponse.admins.find(
        a => a.email === newEmail.trim().toLowerCase() && !a.is_owner
      );
      if (newAdmin) {
        // Update permissions
        await fetchApi<{ success: boolean }>(`/super-admin/admins/${newAdmin.id}`, {
          method: 'PUT',
          body: JSON.stringify({ permissions: newPermissions }),
        });
      }
    }

    setSaving(false);
    setAddModalOpen(false);
    setNewEmail('');
    setNewName('');
    setNewPermissions(DEFAULT_PERMISSIONS);
    loadAdmins();
  }

  async function handleUpdatePermissions() {
    if (!editingAdmin) return;

    setSaving(true);
    setSaveError(null);

    const response = await fetchApi<{ success: boolean; error?: string }>(
      `/super-admin/admins/${editingAdmin.id}`,
      {
        method: 'PUT',
        body: JSON.stringify({ permissions: editPermissions }),
      }
    );

    setSaving(false);

    if (response?.success) {
      setEditModalOpen(false);
      setEditingAdmin(null);
      loadAdmins();
    } else {
      setSaveError(response?.error || 'Failed to update permissions');
    }
  }

  async function handleRemoveAdmin(admin: SuperAdmin) {
    if (!confirm(`Remove ${admin.email} from super admins?`)) {
      return;
    }

    const response = await fetchApi<{ success: boolean; error?: string }>(`/super-admin/admins/${admin.id}`, {
      method: 'DELETE',
    });

    if (response?.success) {
      loadAdmins();
    } else {
      alert(response?.error || 'Failed to remove admin');
    }
  }

  function openEditModal(admin: SuperAdmin) {
    setEditingAdmin(admin);
    setEditPermissions(admin.permissions);
    setEditModalOpen(true);
    setSaveError(null);
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const formatPermissions = (permissions: AdminPermissions) => {
    const enabled = Object.entries(permissions)
      .filter(([, value]) => value)
      .map(([key]) => PERMISSION_LABELS[key as keyof AdminPermissions].label);

    if (enabled.length === Object.keys(permissions).length) {
      return <Badge tone="success">All Permissions</Badge>;
    }
    if (enabled.length === 0) {
      return <Badge tone="critical">No Permissions</Badge>;
    }
    return <Badge tone="info">{`${enabled.length} permissions`}</Badge>;
  };

  const rows = admins.map((admin) => [
    <div key={admin.id}>
      <Text as="span" fontWeight="semibold">{admin.name || admin.email}</Text>
      {admin.name && (
        <>
          <br />
          <Text as="span" tone="subdued" variant="bodySm">{admin.email}</Text>
        </>
      )}
    </div>,
    admin.is_owner ? (
      <Badge key={`role-${admin.id}`} tone="success">Owner</Badge>
    ) : (
      <Badge key={`role-${admin.id}`} tone="info">Admin</Badge>
    ),
    formatPermissions(admin.permissions),
    admin.added_by || '-',
    formatDate(admin.added_at),
    admin.is_owner ? (
      <Text key={`action-${admin.id}`} as="span" tone="subdued">Cannot modify</Text>
    ) : (
      <InlineStack key={`action-${admin.id}`} gap="200">
        <Button size="slim" onClick={() => openEditModal(admin)}>Edit</Button>
        <Button size="slim" tone="critical" onClick={() => handleRemoveAdmin(admin)}>Remove</Button>
      </InlineStack>
    ),
  ]);

  const PermissionCheckboxes = ({
    permissions,
    onChange,
    disabled = false
  }: {
    permissions: AdminPermissions;
    onChange: (permissions: AdminPermissions) => void;
    disabled?: boolean;
  }) => (
    <BlockStack gap="300">
      {(Object.keys(PERMISSION_LABELS) as Array<keyof AdminPermissions>).map((key) => (
        <Checkbox
          key={key}
          label={PERMISSION_LABELS[key].label}
          helpText={PERMISSION_LABELS[key].description}
          checked={permissions[key]}
          disabled={disabled || key === 'canManageAdmins'} // Only owner can manage admins
          onChange={(checked) => onChange({ ...permissions, [key]: checked })}
        />
      ))}
    </BlockStack>
  );

  return (
    <Page
      title="Super Admins"
      primaryAction={{
        icon: PlusIcon, content: 'Add Admin',
        onAction: () => setAddModalOpen(true),
      }}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <BlockStack gap="200">
                <Text as="h2" variant="headingMd">About Super Admins</Text>
                <Text as="p" tone="subdued">
                  Super admins have access to the admin dashboard. The owner account
                  (dthake@ms.vanilla.net.mt) has all permissions and cannot be modified.
                  Other admins can have specific permissions granted.
                </Text>
              </BlockStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            {loading ? (
              <InlineStack align="center">
                <Spinner size="large" />
              </InlineStack>
            ) : (
              <DataTable
                columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text']}
                headings={['User', 'Role', 'Permissions', 'Added By', 'Added At', 'Actions']}
                rows={rows}
              />
            )}
          </Card>
        </Layout.Section>
      </Layout>

      {/* Add Admin Modal */}
      <Modal
        open={addModalOpen}
        onClose={() => {
          setAddModalOpen(false);
          setNewEmail('');
          setNewName('');
          setNewPermissions(DEFAULT_PERMISSIONS);
          setSaveError(null);
        }}
        title="Add Super Admin"
        primaryAction={{
          icon: PlusIcon, content: 'Add Admin',
          onAction: handleAddAdmin,
          loading: saving,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setAddModalOpen(false),
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            {saveError && (
              <Banner tone="critical">{saveError}</Banner>
            )}
            <TextField
              label="Email"
              type="email"
              requiredIndicator value={newEmail}
              onChange={setNewEmail}
              placeholder="admin@example.com"
              autoComplete="email"
              helpText="The user must have a Whenly account (signed in with Google)"
            />
            <TextField
              label="Name (optional)"
              value={newName}
              onChange={setNewName}
              placeholder="John Doe"
              autoComplete="name"
            />
            <Divider />
            <Text as="h3" variant="headingMd">Permissions</Text>
            <PermissionCheckboxes
              permissions={newPermissions}
              onChange={setNewPermissions}
            />
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Edit Permissions Modal */}
      <Modal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setEditingAdmin(null);
          setSaveError(null);
        }}
        title={`Edit Permissions: ${editingAdmin?.name || editingAdmin?.email}`}
        primaryAction={{
          content: 'Save Permissions',
          onAction: handleUpdatePermissions,
          loading: saving,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setEditModalOpen(false),
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            {saveError && (
              <Banner tone="critical">{saveError}</Banner>
            )}
            <Text as="p" tone="subdued">
              Configure what {editingAdmin?.email} can access in the admin dashboard.
            </Text>
            <PermissionCheckboxes
              permissions={editPermissions}
              onChange={setEditPermissions}
            />
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
